/**
 * Helpers for remapping Course/Module/Lesson coordinates when a syllabus lesson moves.
 * Multi-attribution exercises: only the matching slot is rewritten; other slots stay.
 */

export function normCourse(value) {
    return String(value ?? '').trim().toLowerCase();
}

export function sameLessonCoord(a, course, module, lesson) {
    if (!a) return false;
    return (
        normCourse(a.course) === normCourse(course) &&
        String(a.module ?? '') === String(module ?? '') &&
        String(a.lesson ?? '') === String(lesson ?? '')
    );
}

export function coordPayload(course, module, lesson) {
    return {
        course: String(course ?? '').trim(),
        module: String(module ?? ''),
        lesson: String(lesson ?? ''),
    };
}

/**
 * Remap one matching attribution slot (and sync primary columns from attributions[0]).
 * Returns null if nothing matched.
 */
export function remapExerciseAttributionDelta(exercise, course, fromModule, fromLesson, toModule, toLesson) {
    const hasAttrArray = Array.isArray(exercise?.attributions) && exercise.attributions.length > 0;
    const attrs = hasAttrArray
        ? exercise.attributions.map((a) => ({ ...a }))
        : [{
            course: exercise?.course || '',
            module: exercise?.module ?? '',
            lesson: exercise?.lesson ?? '',
        }];

    let changed = false;
    const nextAttrs = attrs.map((a) => {
        if (!sameLessonCoord(a, course, fromModule, fromLesson)) return a;
        changed = true;
        return {
            ...a,
            ...coordPayload(course, toModule, toLesson),
        };
    });

    // Legacy: primary columns match but attributions array did not include that slot
    if (!changed && sameLessonCoord(
        { course: exercise?.course, module: exercise?.module, lesson: exercise?.lesson },
        course,
        fromModule,
        fromLesson
    )) {
        changed = true;
        if (hasAttrArray) {
            nextAttrs.unshift(coordPayload(course, toModule, toLesson));
        } else {
            nextAttrs[0] = coordPayload(course, toModule, toLesson);
        }
    }

    if (!changed) return null;

    const primary = nextAttrs[0] || coordPayload(course, toModule, toLesson);
    return {
        course: primary.course || '',
        module: primary.module ?? '',
        lesson: primary.lesson ?? '',
        attributions: nextAttrs,
    };
}

/** Simple course/module/lesson row (words, blocks, slides, cards). */
export function remapSimpleAttributionDelta(row, course, fromModule, fromLesson, toModule, toLesson, { numeric = false } = {}) {
    if (!sameLessonCoord(row, course, fromModule, fromLesson)) return null;
    if (numeric) {
        return {
            course: String(course ?? '').trim(),
            module: Number(toModule),
            lesson: Number(toLesson),
        };
    }
    return coordPayload(course, toModule, toLesson);
}
